import { state } from '../state.js';
import { AuditLog, SyncJob } from '../types.js';
import { escapeHtml, formatDateTime, formatDuration } from '../utils/formatters.js';
import { showToast } from '../utils/toast.js';

/**
 * Switch Sub-Tab inside Logs & History panel (jobs | stream | errors)
 */
export function switchLogsSubTab(subTab: 'jobs' | 'stream' | 'errors'): void {
  state.logs.activeSubTab = subTab;

  document.querySelectorAll('.subtab-btn').forEach((btn) => {
    const isTarget = btn.getAttribute('data-subtab') === subTab;
    btn.classList.toggle('active', isTarget);
    btn.setAttribute('aria-selected', isTarget ? 'true' : 'false');
  });

  const jobsView = document.getElementById('logs-jobs-view');
  const streamView = document.getElementById('logs-stream-view');
  const errorsView = document.getElementById('logs-errors-view');

  if (jobsView) jobsView.style.display = subTab === 'jobs' ? 'block' : 'none';
  if (streamView) streamView.style.display = subTab === 'stream' ? 'block' : 'none';
  if (errorsView) errorsView.style.display = subTab === 'errors' ? 'block' : 'none';

  if (subTab === 'jobs') {
    fetchJobs();
  } else if (subTab === 'stream') {
    fetchAuditLogs();
  } else if (subTab === 'errors') {
    fetchJobs();
    fetchAuditLogs();
  }
}

/**
 * Live Polling Controller for Sync History & Logs
 */
export function startLogsLivePolling(): void {
  if (state.jobs.liveTimer) return;
  if (!state.jobs.isLivePolling) return;

  state.jobs.liveTimer = setInterval(() => {
    if (state.activeTab === 'logs') {
      if (state.logs.activeSubTab === 'jobs') {
        fetchJobs(true);
      } else if (state.logs.activeSubTab === 'stream') {
        fetchAuditLogs(true);
      } else if (state.logs.activeSubTab === 'errors') {
        fetchJobs(true);
        fetchAuditLogs(true);
      }
    }
  }, 2500);
}

export function stopLogsLivePolling(): void {
  if (state.jobs.liveTimer) {
    clearInterval(state.jobs.liveTimer);
    state.jobs.liveTimer = null;
  }
}

export function toggleLivePolling(): void {
  state.jobs.isLivePolling = !state.jobs.isLivePolling;
  const liveDot = document.getElementById('live-status-dot');
  const liveText = document.getElementById('live-status-text');

  if (state.jobs.isLivePolling) {
    if (liveDot) liveDot.className = 'live-dot active';
    if (liveText) liveText.textContent = 'Live: ON';
    startLogsLivePolling();
    showToast('Live stream polling enabled (2.5s)', 'info', 2000);
  } else {
    if (liveDot) liveDot.className = 'live-dot';
    if (liveText) liveText.textContent = 'Live: OFF';
    stopLogsLivePolling();
    showToast('Live stream polling paused', 'info', 2000);
  }
}

/**
 * Fetch and update Job Statistics
 */
export async function fetchJobStats(): Promise<void> {
  try {
    const res = await fetch('/api/v1/jobs/stats');
    if (!res.ok) return;
    const data = await res.json();
    const stats = data.stats || {};
    state.jobs.stats = stats;

    const elTotal = document.getElementById('metric-total-jobs');
    const elRunning = document.getElementById('metric-running-jobs');
    const elRunningBadge = document.getElementById('metric-running-jobs-badge');
    const elCompleted = document.getElementById('metric-completed-jobs');
    const elSuccessRate = document.getElementById('metric-success-rate');
    const elSuccessPercent = document.getElementById('metric-success-percent');
    const elFailed = document.getElementById('metric-failed-jobs');
    const elCancelled = document.getElementById('metric-cancelled-jobs');
    const elFailedBadge = document.getElementById('metric-failed-badge');
    const elProcessed = document.getElementById('metric-processed-entities');
    const elTotalEntitiesBadge = document.getElementById('metric-total-entities-badge');
    const elMiniCreated = document.getElementById('metric-mini-created');
    const elMiniUpdated = document.getElementById('metric-mini-updated');
    const elMiniFailed = document.getElementById('metric-mini-failed');
    const elSubtabJobsCount = document.getElementById('subtab-jobs-count');

    if (elTotal) elTotal.textContent = String(stats.total || 0);
    if (elRunning) elRunning.textContent = `${stats.running || 0} active`;
    if (elRunningBadge) elRunningBadge.textContent = `${stats.running || 0} Running`;

    if (elCompleted) elCompleted.textContent = String(stats.completed || 0);
    const totalFinished = (stats.completed || 0) + (stats.failed || 0);
    const rate = totalFinished > 0 ? Math.round(((stats.completed || 0) / totalFinished) * 100) : 100;
    if (elSuccessRate) elSuccessRate.textContent = `${rate}%`;
    if (elSuccessPercent) elSuccessPercent.textContent = `${rate}%`;

    if (elFailed) elFailed.textContent = String(stats.failed || 0);
    if (elCancelled) elCancelled.textContent = `${stats.cancelled || 0} cancelled`;
    if (elFailedBadge) {
      elFailedBadge.textContent = stats.failed > 0 ? `${stats.failed} Failed` : '0 Issues';
      elFailedBadge.className = stats.failed > 0 ? 'badge badge-danger' : 'badge badge-neutral';
    }

    if (elProcessed) elProcessed.textContent = (stats.processed_entities || 0).toLocaleString();
    if (elTotalEntitiesBadge) elTotalEntitiesBadge.textContent = `${(stats.total_entities || 0).toLocaleString()} Total`;
    if (elMiniCreated) elMiniCreated.textContent = `+${stats.created_count || 0} new`;
    if (elMiniUpdated) elMiniUpdated.textContent = `~${stats.updated_count || 0} upd`;
    if (elMiniFailed) elMiniFailed.textContent = `!${stats.failed_count || 0} fail`;
    if (elSubtabJobsCount) elSubtabJobsCount.textContent = String(stats.total || 0);
  } catch (err) {
    console.error('Failed fetching job stats:', err);
  }
}

/**
 * Fetch Sync Jobs list
 */
export async function fetchJobs(background: boolean = false): Promise<void> {
  try {
    const params = new URLSearchParams();
    if (state.jobs.filterStatus) params.set('status', state.jobs.filterStatus);
    if (state.jobs.filterType) params.set('job_type', state.jobs.filterType);
    params.set('limit', state.jobs.limit.toString());
    params.set('offset', ((state.jobs.page - 1) * state.jobs.limit).toString());

    const res = await fetch(`/api/v1/jobs?${params.toString()}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    let jobs: SyncJob[] = data.jobs || [];
    if (state.jobs.search) {
      const q = state.jobs.search.toLowerCase();
      jobs = jobs.filter((j) =>
        (j.id && j.id.toLowerCase().includes(q)) ||
        (j.job_type && j.job_type.toLowerCase().includes(q)) ||
        (j.error_message && j.error_message.toLowerCase().includes(q)) ||
        (j.source_connection_id && j.source_connection_id.toLowerCase().includes(q)) ||
        (j.target_connection_id && j.target_connection_id.toLowerCase().includes(q))
      );
    }

    state.jobs.list = jobs;
    state.jobs.total = data.total || jobs.length;

    renderJobsTable();
    await fetchJobStats();

    if (state.logs.activeSubTab === 'errors') {
      renderErrorDiagnostics();
    }
  } catch (err) {
    if (!background) {
      console.error('Failed fetching jobs:', err);
      showToast('Failed to load sync jobs history', 'error');
    }
  }
}

/**
 * Render Sync Jobs Data Table
 */
export function renderJobsTable(): void {
  const tbody = document.getElementById('jobs-table-body');
  const emptyState = document.getElementById('jobs-empty-state');
  const infoText = document.getElementById('jobs-table-info');
  if (!tbody) return;

  tbody.innerHTML = '';

  const jobs = state.jobs.list;

  if (jobs.length === 0) {
    if (emptyState) emptyState.style.display = 'flex';
    if (infoText) infoText.textContent = 'Showing 0 of 0 jobs';
    return;
  }

  if (emptyState) emptyState.style.display = 'none';
  if (infoText) {
    infoText.textContent = `Showing ${jobs.length} of ${state.jobs.total} jobs`;
  }

  jobs.forEach((job) => {
    const tr = document.createElement('tr');
    tr.className = job.status === 'running' ? 'row-running' : job.status === 'failed' ? 'row-failed' : '';

    // Duration calculation
    let durationText = '--';
    if (job.started_at) {
      const start = new Date(job.started_at).getTime();
      const end = job.completed_at ? new Date(job.completed_at).getTime() : Date.now();
      durationText = formatDuration(end - start);
    }

    // Progress %
    const total = Number(job.total_entities || 0);
    const processed = Number(job.processed_entities || 0);
    const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : job.status === 'completed' ? 100 : 0;

    // Progress bar fill color class
    const fillClass = job.status === 'completed' ? 'fill-completed' : job.status === 'failed' ? 'fill-failed' : '';

    // Status badge inner HTML
    let statusBadgeHtml = '';
    if (job.status === 'running') {
      statusBadgeHtml = `<span class="status-badge-inline status-badge-running"><span class="spinner-icon"></span> Running</span>`;
    } else if (job.status === 'completed') {
      statusBadgeHtml = `<span class="status-badge-inline status-badge-completed">✓ Completed</span>`;
    } else if (job.status === 'failed') {
      statusBadgeHtml = `<span class="status-badge-inline status-badge-failed">✕ Failed</span>`;
    } else if (job.status === 'cancelled') {
      statusBadgeHtml = `<span class="status-badge-inline status-badge-cancelled">⊘ Cancelled</span>`;
    } else {
      statusBadgeHtml = `<span class="status-badge-inline status-badge-pending">⏳ Pending</span>`;
    }

    tr.innerHTML = `
      <td>
        <div class="job-id-cell">
          <span class="job-id-link" data-job-id="${escapeHtml(job.id)}">${escapeHtml(job.id)}</span>
          <span class="job-type-badge job-type-${escapeHtml(job.job_type)}">${escapeHtml(job.job_type)}</span>
        </div>
      </td>
      <td>${statusBadgeHtml}</td>
      <td>
        <div class="progress-cell-wrapper">
          <div class="progress-meta-text">
            <strong class="mono">${percent}%</strong>
            <span class="mono">${processed.toLocaleString()} / ${total.toLocaleString()}</span>
          </div>
          <div class="progress-bar-sm">
            <div class="progress-bar-fill ${fillClass}" style="width: ${percent}%;"></div>
          </div>
        </div>
      </td>
      <td>
        <div class="entity-counts-cell">
          <span class="pill-mini pill-created" title="Created Entities">+${job.created_count || 0}</span>
          <span class="pill-mini pill-updated" title="Updated Entities">~${job.updated_count || 0}</span>
          <span class="pill-mini pill-failed" title="Failed Entities">!${job.failed_count || 0}</span>
          <span class="pill-mini pill-skipped" title="Skipped Entities">↷${job.skipped_count || 0}</span>
        </div>
      </td>
      <td>
        <div style="display: flex; flex-direction: column; gap: 0.15rem;">
          <span class="mono" style="font-weight: 500;">${durationText}</span>
          <span style="font-size: 0.75rem; color: var(--text-muted);">${formatDateTime(job.started_at || job.created_at)}</span>
        </div>
      </td>
      <td>
        <div class="actions-cell-inline">
          <button type="button" class="btn-table-action" data-action="view" data-job-id="${escapeHtml(job.id)}" title="Inspect Job Details & Logs">Details</button>
          ${(Number(job.failed_count || 0) > 0 || job.status === 'failed')
            ? `<button type="button" class="btn-table-action btn-action-warning" data-action="retry" data-job-id="${escapeHtml(job.id)}" title="Retry ${job.failed_count || 0} failed entity items">↻ Retry</button>`
            : ''
          }
          ${job.status === 'running' || job.status === 'pending'
            ? `<button type="button" class="btn-table-action btn-action-danger" data-action="cancel" data-job-id="${escapeHtml(job.id)}" title="Cancel Job">Cancel</button>`
            : `<button type="button" class="btn-table-action btn-action-danger" data-action="delete" data-job-id="${escapeHtml(job.id)}" title="Delete Job Record">Delete</button>`
          }
        </div>
      </td>
    `;

    // Row Click & Action button bindings
    tr.querySelectorAll('[data-job-id]').forEach((elem) => {
      const jId = elem.getAttribute('data-job-id');
      const action = elem.getAttribute('data-action');
      if (!jId) return;
      if (action === 'view' || elem.classList.contains('job-id-link')) {
        elem.addEventListener('click', () => openJobDetailModal(jId));
      } else if (action === 'retry') {
        elem.addEventListener('click', () => retryJob(jId));
      } else if (action === 'cancel') {
        elem.addEventListener('click', () => cancelJob(jId));
      } else if (action === 'delete') {
        elem.addEventListener('click', () => deleteJob(jId));
      }
    });

    tbody.appendChild(tr);
  });
}

/**
 * Open Job Detail Modal and load live progress + audit logs
 */
export async function openJobDetailModal(jobId: string): Promise<void> {
  state.jobs.selectedJobId = jobId;
  const modal = document.getElementById('job-detail-modal');
  if (!modal) return;

  try {
    const res = await fetch(`/api/v1/jobs/${encodeURIComponent(jobId)}`);
    if (!res.ok) throw new Error('Job not found');
    const data = await res.json();
    const job: SyncJob = data.job;
    state.jobs.selectedJob = job;

    // Update Header & Tags
    const elId = document.getElementById('job-modal-id-text');
    const elType = document.getElementById('job-modal-type-badge');
    const elStatus = document.getElementById('job-modal-status-badge');
    if (elId) elId.textContent = `ID: ${job.id}`;
    if (elType) {
      elType.textContent = job.job_type;
      elType.className = `badge badge-accent job-type-${job.job_type}`;
    }
    if (elStatus) {
      elStatus.textContent = job.status;
      elStatus.className = `badge ${job.status === 'completed' ? 'badge-success' : job.status === 'failed' ? 'badge-danger' : job.status === 'running' ? 'badge-accent' : 'badge-neutral'}`;
    }

    // Progress
    const total = Number(job.total_entities || 0);
    const processed = Number(job.processed_entities || 0);
    const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : job.status === 'completed' ? 100 : 0;
    const elBar = document.getElementById('job-modal-progress-bar');
    const elPercent = document.getElementById('job-modal-progress-percent');
    const elCounts = document.getElementById('job-modal-progress-counts');
    if (elBar) {
      elBar.style.width = `${percent}%`;
      elBar.className = `progress-bar-fill ${job.status === 'completed' ? 'fill-completed' : job.status === 'failed' ? 'fill-failed' : ''}`;
    }
    if (elPercent) elPercent.textContent = `${percent}%`;
    if (elCounts) elCounts.textContent = `(${processed.toLocaleString()} / ${total.toLocaleString()} entities)`;

    // Metadata Grid
    const elSource = document.getElementById('job-modal-source');
    const elTarget = document.getElementById('job-modal-target');
    const elStarted = document.getElementById('job-modal-started');
    const elDuration = document.getElementById('job-modal-duration');

    if (elSource) elSource.textContent = job.source_connection_id || 'default (Active Source)';
    if (elTarget) elTarget.textContent = job.target_connection_id || 'default (Active Target)';
    if (elStarted) elStarted.textContent = formatDateTime(job.started_at || job.created_at);

    let durationText = '--';
    if (job.started_at) {
      const start = new Date(job.started_at).getTime();
      const end = job.completed_at ? new Date(job.completed_at).getTime() : Date.now();
      durationText = formatDuration(end - start);
    }
    if (elDuration) elDuration.textContent = job.completed_at ? `${formatDateTime(job.completed_at)} (${durationText})` : durationText;

    // Breakdown Matrix
    const elTotalMatrix = document.getElementById('job-modal-total-count');
    const elProcessedMatrix = document.getElementById('job-modal-processed-count');
    const elCreatedMatrix = document.getElementById('job-modal-created-count');
    const elUpdatedMatrix = document.getElementById('job-modal-updated-count');
    const elFailedMatrix = document.getElementById('job-modal-failed-count');
    const elSkippedMatrix = document.getElementById('job-modal-skipped-count');

    if (elTotalMatrix) elTotalMatrix.textContent = (job.total_entities || 0).toLocaleString();
    if (elProcessedMatrix) elProcessedMatrix.textContent = (job.processed_entities || 0).toLocaleString();
    if (elCreatedMatrix) elCreatedMatrix.textContent = (job.created_count || 0).toLocaleString();
    if (elUpdatedMatrix) elUpdatedMatrix.textContent = (job.updated_count || 0).toLocaleString();
    if (elFailedMatrix) elFailedMatrix.textContent = (job.failed_count || 0).toLocaleString();
    if (elSkippedMatrix) elSkippedMatrix.textContent = (job.skipped_count || 0).toLocaleString();

    // Entity types
    const elEntityTypes = document.getElementById('job-modal-entity-types');
    if (elEntityTypes) {
      elEntityTypes.innerHTML = '';
      let rawTypes = job.entity_types;
      let types: string[] = [];
      if (typeof rawTypes === 'string') {
        try {
          const parsed = JSON.parse(rawTypes);
          types = Array.isArray(parsed) ? parsed : [rawTypes];
        } catch {
          types = [rawTypes];
        }
      } else if (Array.isArray(rawTypes)) {
        types = rawTypes;
      }
      if (types.length > 0) {
        types.forEach((t) => {
          const pill = document.createElement('span');
          pill.className = 'entity-type-pill';
          pill.textContent = String(t);
          elEntityTypes.appendChild(pill);
        });
      } else {
        elEntityTypes.innerHTML = '<span class="text-muted" style="font-size: 0.8rem;">All standard catalog entity types</span>';
      }
    }

    // Error banner
    const elErrorBox = document.getElementById('job-modal-error-box');
    const elErrorMsg = document.getElementById('job-modal-error-message');
    if (job.error_message) {
      if (elErrorBox) elErrorBox.style.display = 'block';
      if (elErrorMsg) elErrorMsg.textContent = job.error_message;
    } else {
      if (elErrorBox) elErrorBox.style.display = 'none';
    }

    // Failed Entity Items section
    const failedGroup = document.getElementById('job-modal-failed-items-group');
    const failedList = document.getElementById('job-modal-failed-items-list');
    const failedCount = document.getElementById('job-modal-failed-items-count');
    const btnRetryAllInModal = document.getElementById('btn-job-modal-retry-all');

    if (btnRetryAllInModal) {
      btnRetryAllInModal.onclick = () => retryJob(job.id);
    }

    try {
      const failedRes = await fetch(`/api/v1/jobs/${encodeURIComponent(jobId)}/failed-items`);
      if (failedRes.ok) {
        const failedData = await failedRes.json();
        const items = failedData.failed_items || [];

        if (items.length > 0) {
          if (failedGroup) failedGroup.style.display = 'block';
          if (failedCount) failedCount.textContent = String(items.length);
          if (failedList) {
            failedList.innerHTML = '';
            items.forEach((item: any) => {
              const row = document.createElement('div');
              row.className = 'failed-item-row';
              row.innerHTML = `
                <div class="failed-item-info">
                  <span class="failed-item-badge">${escapeHtml(item.entity_type)}</span>
                  <span class="failed-item-id">${escapeHtml(item.entity_id)}</span>
                  <span class="failed-item-err">${escapeHtml(item.error_message)}</span>
                </div>
                <button type="button" class="btn btn-xs btn-outline-warning btn-retry-single-item" title="Retry this entity item directly">
                  <span>↻ Retry</span>
                </button>
              `;

              const btnRetrySingle = row.querySelector('.btn-retry-single-item');
              if (btnRetrySingle) {
                btnRetrySingle.addEventListener('click', () => {
                  retrySingleEntityItem(item.entity_type, item.entity_id, job.id);
                });
              }

              failedList.appendChild(row);
            });
          }
        } else {
          if (failedGroup) failedGroup.style.display = 'none';
        }
      } else {
        if (failedGroup) failedGroup.style.display = 'none';
      }
    } catch {
      if (failedGroup) failedGroup.style.display = 'none';
    }

    // Actions
    const btnCancel = document.getElementById('btn-modal-cancel-job');
    if (btnCancel) {
      btnCancel.style.display = (job.status === 'running' || job.status === 'pending') ? 'inline-flex' : 'none';
    }

    const btnRetryJob = document.getElementById('btn-modal-retry-job');
    if (btnRetryJob) {
      const hasFailures = Number(job.failed_count || 0) > 0 || job.status === 'failed';
      btnRetryJob.style.display = hasFailures ? 'inline-flex' : 'none';
      btnRetryJob.onclick = () => retryJob(job.id);
    }

    // Load Job Logs
    await fetchJobAuditLogs(jobId);

    modal.style.display = 'flex';
  } catch (err: any) {
    showToast(`Failed loading job: ${err.message}`, 'error');
  }
}

/**
 * Fetch and render audit logs for a specific job inside the modal
 */
export async function fetchJobAuditLogs(jobId: string): Promise<void> {
  const container = document.getElementById('job-modal-logs-list');
  const countBadge = document.getElementById('job-modal-logs-count');
  if (!container) return;

  try {
    const res = await fetch(`/api/v1/jobs/${encodeURIComponent(jobId)}/logs?limit=100`);
    if (!res.ok) return;
    const data = await res.json();
    const logs: AuditLog[] = data.logs || [];

    if (countBadge) countBadge.textContent = `${logs.length} events`;
    container.innerHTML = '';

    if (logs.length === 0) {
      container.innerHTML = '<div style="padding: 1rem; color: var(--text-muted); font-size: 0.8rem; text-align: center;">No audit logs recorded for this job yet.</div>';
      return;
    }

    logs.forEach((log) => {
      const item = document.createElement('div');
      item.className = 'audit-log-item';
      item.innerHTML = `
        <div class="log-item-header">
          <span class="log-level-badge level-${escapeHtml(log.level)}">${escapeHtml(log.level)}</span>
          <span class="log-timestamp">${formatDateTime(log.created_at)}</span>
          <span class="log-category-tag">[${escapeHtml(log.category)}]</span>
          <span class="log-action-tag">${escapeHtml(log.action)}</span>
          ${log.entity_type ? `<span class="log-entity-tag">${escapeHtml(log.entity_type)}${log.entity_id ? `:${escapeHtml(log.entity_id)}` : ''}</span>` : ''}
          <span class="log-message-text">${escapeHtml(log.message)}</span>
        </div>
        ${log.details ? `<div class="log-item-details mono">${typeof log.details === 'string' ? escapeHtml(log.details) : escapeHtml(JSON.stringify(log.details, null, 2))}</div>` : ''}
      `;
      container.appendChild(item);
    });
  } catch (err) {
    console.error('Failed fetching job logs:', err);
  }
}

export function closeJobDetailModal(): void {
  const modal = document.getElementById('job-detail-modal');
  if (modal) modal.style.display = 'none';
  state.jobs.selectedJobId = null;
  state.jobs.selectedJob = null;
}

/**
 * Cancel an in-flight sync job
 */
export async function cancelJob(jobId: string): Promise<void> {
  try {
    const res = await fetch(`/api/v1/jobs/${encodeURIComponent(jobId)}/cancel`, { method: 'POST' });
    if (!res.ok) throw new Error('Cancellation failed');
    showToast(`Job ${jobId} cancelled successfully`, 'warning');
    await fetchJobs();
    if (state.jobs.selectedJobId === jobId) {
      openJobDetailModal(jobId);
    }
  } catch (err: any) {
    showToast(`Failed to cancel job: ${err.message}`, 'error');
  }
}

/**
 * Delete a sync job record
 */
export async function deleteJob(jobId: string): Promise<void> {
  if (!confirm(`Are you sure you want to delete job record ${jobId}?`)) return;
  try {
    const res = await fetch(`/api/v1/jobs/${encodeURIComponent(jobId)}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Delete failed');
    showToast(`Job ${jobId} deleted`, 'info');
    closeJobDetailModal();
    await fetchJobs();
  } catch (err: any) {
    showToast(`Failed to delete job: ${err.message}`, 'error');
  }
}

/**
 * Simulate / Demo a Sync Job with live progress & logs stream
 */
export async function createSampleJob(): Promise<void> {
  const jobId = `job_sync_${Date.now().toString().slice(-6)}`;
  const total = 120;

  showToast(`Simulating Sync Job '${jobId}'...`, 'info', 3000);

  try {
    // 1. Create Job in 'running' state
    await fetch('/api/v1/jobs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: jobId,
        job_type: 'batch_sync',
        status: 'running',
        total_entities: total,
        processed_entities: 0,
        entity_types: ['system_config', 'attributes', 'categories', 'products'],
      }),
    });

    await fetchJobs();

    // 2. Incremental progress simulation
    let current = 0;
    const stages = [
      { type: 'system_config', count: 10, action: 'sync_config', msg: 'Synchronized system channel and currency settings' },
      { type: 'attributes', count: 30, action: 'sync_attributes', msg: 'Transferred 30 product attributes and option values' },
      { type: 'categories', count: 20, action: 'sync_categories', msg: 'Resolved category tree topological dependencies' },
      { type: 'products', count: 60, action: 'sync_products', msg: 'Streamed product models, assets, and variants with SHA-256 diffing' },
    ];

    let stageIdx = 0;
    const interval = setInterval(async () => {
      if (stageIdx >= stages.length) {
        clearInterval(interval);
        // Complete job
        await fetch(`/api/v1/jobs/${jobId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status: 'completed',
            processed_entities: total,
            created_count: 85,
            updated_count: 35,
          }),
        });

        await fetch('/api/v1/logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            job_id: jobId,
            level: 'info',
            category: 'sync',
            action: 'job_completed',
            message: `Sync job '${jobId}' completed successfully (120 entities synced)`,
          }),
        });

        showToast(`Sync Job '${jobId}' Completed!`, 'success');
        await fetchJobs();
        await fetchAuditLogs();
        return;
      }

      const st = stages[stageIdx];
      current += st.count;
      stageIdx++;

      await fetch(`/api/v1/jobs/${jobId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          processed_entities: current,
          created_count: Math.round(current * 0.7),
          updated_count: Math.round(current * 0.3),
        }),
      });

      await fetch('/api/v1/logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: jobId,
          level: 'info',
          category: 'sync',
          entity_type: st.type,
          action: st.action,
          message: st.msg,
          details: { phase: st.type, progress: `${current}/${total}` },
        }),
      });

      fetchJobs(true);
      if (state.logs.activeSubTab === 'stream') {
        fetchAuditLogs(true);
      }
    }, 600);
  } catch (err: any) {
    showToast(`Simulation failed: ${err.message}`, 'error');
  }
}

/**
 * Fetch and render System Audit Logs
 */
export async function fetchAuditLogs(background: boolean = false): Promise<void> {
  try {
    const params = new URLSearchParams();
    if (state.logs.filterLevel) params.set('level', state.logs.filterLevel);
    if (state.logs.filterCategory) params.set('category', state.logs.filterCategory);
    if (state.logs.search) params.set('search', state.logs.search);
    params.set('limit', state.logs.limit.toString());

    const [logsRes, statsRes] = await Promise.all([
      fetch(`/api/v1/logs?${params.toString()}`),
      fetch('/api/v1/logs/stats'),
    ]);

    if (!logsRes.ok) throw new Error(`HTTP ${logsRes.status}`);
    const logsData = await logsRes.json();
    const statsData = statsRes.ok ? await statsRes.json() : { stats: {} };

    state.logs.list = logsData.logs || [];
    state.logs.total = logsData.total || state.logs.list.length;
    state.logs.stats = statsData.stats || {};

    // Update Chip counts
    const s = state.logs.stats;
    const elChipAll = document.getElementById('count-chip-all');
    const elChipError = document.getElementById('count-chip-error');
    const elChipWarn = document.getElementById('count-chip-warn');
    const elChipInfo = document.getElementById('count-chip-info');
    const elChipDebug = document.getElementById('count-chip-debug');
    const elSubtabStreamCount = document.getElementById('subtab-stream-count');
    const elSubtabErrorsCount = document.getElementById('subtab-errors-count');
    const elTotalLogs = document.getElementById('metric-total-logs');
    const elErrorLogs = document.getElementById('metric-error-logs');

    if (elChipAll) elChipAll.textContent = String(s.total || 0);
    if (elChipError) elChipError.textContent = String(s.error || 0);
    if (elChipWarn) elChipWarn.textContent = String(s.warn || 0);
    if (elChipInfo) elChipInfo.textContent = String(s.info || 0);
    if (elChipDebug) elChipDebug.textContent = String(s.debug || 0);
    if (elSubtabStreamCount) elSubtabStreamCount.textContent = String(s.total || 0);
    if (elSubtabErrorsCount) elSubtabErrorsCount.textContent = String((s.error || 0) + (state.jobs.stats.failed || 0));
    if (elTotalLogs) elTotalLogs.textContent = String(s.total || 0);
    if (elErrorLogs) elErrorLogs.textContent = `${s.error || 0} err / ${s.warn || 0} warn`;

    renderAuditLogs();
  } catch (err) {
    if (!background) {
      console.error('Failed fetching audit logs:', err);
      showToast('Failed to load audit logs', 'error');
    }
  }
}

/**
 * Render Audit Log Stream
 */
export function renderAuditLogs(): void {
  const container = document.getElementById('audit-log-stream-list');
  const emptyState = document.getElementById('logs-empty-state');
  const infoText = document.getElementById('logs-table-info');
  const viewer = document.getElementById('audit-stream-viewer');
  if (!container) return;

  container.innerHTML = '';
  const logs = state.logs.list;

  if (logs.length === 0) {
    if (emptyState) emptyState.style.display = 'flex';
    if (infoText) infoText.textContent = 'Showing 0 of 0 log entries';
    return;
  }

  if (emptyState) emptyState.style.display = 'none';
  if (infoText) {
    infoText.textContent = `Showing ${logs.length} of ${state.logs.total} log entries`;
  }

  logs.forEach((log) => {
    const item = document.createElement('div');
    item.className = 'audit-log-item';
    item.innerHTML = `
      <div class="log-item-header">
        <span class="log-level-badge level-${escapeHtml(log.level)}">${escapeHtml(log.level)}</span>
        <span class="log-timestamp">${formatDateTime(log.created_at)}</span>
        <span class="log-category-tag">[${escapeHtml(log.category)}]</span>
        <span class="log-action-tag">${escapeHtml(log.action)}</span>
        ${log.job_id ? `<span class="job-type-badge" style="cursor: pointer;" onclick="window.AkeneoDashboard.openJobDetail('${escapeHtml(log.job_id)}')">${escapeHtml(log.job_id)}</span>` : ''}
        ${log.entity_type ? `<span class="log-entity-tag">${escapeHtml(log.entity_type)}${log.entity_id ? `:${escapeHtml(log.entity_id)}` : ''}</span>` : ''}
        <span class="log-message-text">${escapeHtml(log.message)}</span>
      </div>
      ${log.details ? `<div class="log-item-details mono">${typeof log.details === 'string' ? escapeHtml(log.details) : escapeHtml(JSON.stringify(log.details, null, 2))}</div>` : ''}
    `;
    container.appendChild(item);
  });

  const autoScrollToggle = document.getElementById('log-autoscroll-toggle') as HTMLInputElement | null;
  if (viewer && autoScrollToggle && autoScrollToggle.checked) {
    viewer.scrollTop = viewer.scrollHeight;
  }
}

/**
 * Render Error & Failure Diagnostics Center
 */
export function renderErrorDiagnostics(): void {
  const container = document.getElementById('diagnostics-container');
  if (!container) return;

  const failedJobs = state.jobs.list.filter((j) => j.status === 'failed');
  const errorLogs = state.logs.list.filter((l) => l.level === 'error');

  container.innerHTML = '';

  if (failedJobs.length === 0 && errorLogs.length === 0) {
    container.innerHTML = `
      <div class="table-empty-state" style="padding: 2rem 1rem;">
        <div class="empty-state-icon">✅</div>
        <h3>No System Errors or Failures Detected</h3>
        <p>All synchronization executions and API workflows are operating cleanly with zero errors.</p>
      </div>
    `;
    return;
  }

  // Failed Jobs Section
  if (failedJobs.length > 0) {
    const jobsGroup = document.createElement('div');
    jobsGroup.innerHTML = `<h4 style="margin-bottom: 0.75rem; color: #f87171;">Failed Synchronization Jobs (${failedJobs.length})</h4>`;

    failedJobs.forEach((job) => {
      const card = document.createElement('div');
      card.className = 'diagnostic-item-card';
      card.innerHTML = `
        <div class="diagnostic-header">
          <div class="diagnostic-title">
            <span>⚡ ${escapeHtml(job.id)}</span>
            <span class="badge badge-danger">Failed</span>
            <span class="job-type-badge">${escapeHtml(job.job_type)}</span>
          </div>
          <div style="display: flex; gap: 0.5rem;">
            <button class="btn btn-sm btn-warning" onclick="window.AkeneoDashboard.retryJob('${escapeHtml(job.id)}')">↻ Retry Failed Items</button>
            <button class="btn btn-sm btn-secondary" onclick="window.AkeneoDashboard.openJobDetail('${escapeHtml(job.id)}')">View Job Logs</button>
          </div>
        </div>
        <div class="diagnostic-msg">${escapeHtml(job.error_message || 'Job terminated due to unexpected error')}</div>
        <div style="font-size: 0.75rem; color: var(--text-muted);">
          Started: ${formatDateTime(job.started_at)} • Failed: ${formatDateTime(job.completed_at)} • Processed: ${job.processed_entities}/${job.total_entities} entities (${job.failed_count || 0} failed)
        </div>
      `;
      jobsGroup.appendChild(card);
    });
    container.appendChild(jobsGroup);
  }

  // Error Logs Section
  if (errorLogs.length > 0) {
    const logsGroup = document.createElement('div');
    logsGroup.innerHTML = `<h4 style="margin-top: 1.5rem; margin-bottom: 0.75rem; color: #f87171;">Error Audit Log Events (${errorLogs.length})</h4>`;

    errorLogs.forEach((log) => {
      const card = document.createElement('div');
      card.className = 'diagnostic-item-card';
      const canRetryEntity = Boolean(log.entity_type && log.entity_id);
      card.innerHTML = `
        <div class="diagnostic-header">
          <div class="diagnostic-title">
            <span>[${escapeHtml(log.category)}] ${escapeHtml(log.action)}</span>
            ${log.entity_type ? `<span class="badge badge-accent">${escapeHtml(log.entity_type)}${log.entity_id ? `:${escapeHtml(log.entity_id)}` : ''}</span>` : ''}
          </div>
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            ${canRetryEntity ? `<button class="btn btn-xs btn-outline-warning" onclick="window.AkeneoDashboard.retrySingleItem('${escapeHtml(log.entity_type!)}', '${escapeHtml(log.entity_id!)}', '${escapeHtml(log.job_id || '')}')">↻ Retry Entity</button>` : ''}
            <span style="font-size: 0.75rem; color: var(--text-muted);">${formatDateTime(log.created_at)}</span>
          </div>
        </div>
        <div class="diagnostic-msg">${escapeHtml(log.message)}</div>
        ${log.details ? `<div class="log-item-details mono">${typeof log.details === 'string' ? escapeHtml(log.details) : escapeHtml(JSON.stringify(log.details, null, 2))}</div>` : ''}
      `;
      logsGroup.appendChild(card);
    });
    container.appendChild(logsGroup);
  }
}

/**
 * Partial Retry: Retry failed items for a specific job
 */
export async function retryJob(jobId: string, options: Record<string, unknown> = {}): Promise<void> {
  try {
    showToast(`Initiating retry for failed items in job ${jobId}...`, 'info');
    const res = await fetch(`/api/v1/jobs/${encodeURIComponent(jobId)}/retry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ async: true, ...options }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Retry failed');

    showToast(`Retry job '${data.retryJobId || data.retry_job_id}' started for ${data.retriedItemsCount || data.retried_items_count || 0} entities`, 'success');
    await fetchJobs();
    await fetchAuditLogs();
    await fetchJobStats();
    if (state.jobs.selectedJobId === jobId) {
      closeJobDetailModal();
    }
  } catch (err: any) {
    showToast(`Retry failed: ${err.message}`, 'error');
  }
}

/**
 * Partial Retry: Retry a single entity item
 */
export async function retrySingleEntityItem(entityType: string, entityId: string, jobId?: string): Promise<void> {
  try {
    showToast(`Retrying ${entityType} '${entityId}'...`, 'info');
    const res = await fetch('/api/v1/sync/retry-item', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entity_type: entityType,
        entity_id: entityId,
        job_id: jobId,
        async: true,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Retry item failed');

    showToast(`Retry job '${data.retryJobId || data.retry_job_id}' queued for ${entityType} '${entityId}'`, 'success');
    await fetchJobs();
    await fetchAuditLogs();
    await fetchJobStats();
  } catch (err: any) {
    showToast(`Failed retrying item: ${err.message}`, 'error');
  }
}

/**
 * Partial Retry: Bulk retry all failed items from SQLite database
 */
export async function retryAllFailedEntities(): Promise<void> {
  try {
    showToast('Initiating retry for all failed entities from database...', 'info');
    const res = await fetch('/api/v1/sync/retry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ async: true, force: true }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Retry all failed');

    showToast(`Retry job '${data.retryJobId || data.retry_job_id}' queued for ${data.retriedItemsCount || data.retried_items_count || 0} entities across ${data.retriedEntityTypes?.length || 0} types`, 'success');
    await fetchJobs();
    await fetchAuditLogs();
    await fetchJobStats();
  } catch (err: any) {
    showToast(`Failed retrying all items: ${err.message}`, 'error');
  }
}

/**
 * Export current logs as JSON or CSV file
 */
export function exportLogs(format: 'json' | 'csv' = 'json'): void {
  const logs = state.logs.list;
  if (!logs || logs.length === 0) {
    showToast('No logs available to export', 'warning');
    return;
  }

  let content = '';
  let mimeType = 'application/json';
  let filename = `akeneo_audit_logs_${new Date().toISOString().slice(0, 10)}.json`;

  if (format === 'csv') {
    mimeType = 'text/csv';
    filename = `akeneo_audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
    const headers = ['ID', 'Timestamp', 'Level', 'Category', 'Job ID', 'Entity Type', 'Entity ID', 'Action', 'Message'];
    const rows = logs.map((l) => [
      l.id || '',
      l.created_at || '',
      l.level || '',
      l.category || '',
      l.job_id || '',
      l.entity_type || '',
      l.entity_id || '',
      `"${(l.action || '').replace(/"/g, '""')}"`,
      `"${(l.message || '').replace(/"/g, '""')}"`,
    ]);
    content = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  } else {
    content = JSON.stringify(logs, null, 2);
  }

  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  showToast(`Exported ${logs.length} logs as ${filename}`, 'success');
}

/**
 * Modal handlers for Clearing / Pruning Logs
 */
export function openClearLogsModal(): void {
  const modal = document.getElementById('clear-logs-modal');
  if (modal) modal.style.display = 'flex';
}

export function closeClearLogsModal(): void {
  const modal = document.getElementById('clear-logs-modal');
  if (modal) modal.style.display = 'none';
}

export async function confirmClearLogs(): Promise<void> {
  const rangeSelect = document.getElementById('clear-logs-range') as HTMLSelectElement | null;
  const scope = rangeSelect ? rangeSelect.value : 'all';

  let beforeDate: string | null = null;
  if (scope === '7d') {
    beforeDate = new Date(Date.now() - 7 * 86400000).toISOString();
  } else if (scope === '30d') {
    beforeDate = new Date(Date.now() - 30 * 86400000).toISOString();
  }

  try {
    const url = beforeDate ? `/api/v1/logs?before_date=${encodeURIComponent(beforeDate)}` : '/api/v1/logs';
    const res = await fetch(url, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to prune logs');
    const data = await res.json();
    showToast(data.message || 'Logs cleared successfully', 'success');
    closeClearLogsModal();
    await fetchAuditLogs();
  } catch (err: any) {
    showToast(`Error clearing logs: ${err.message}`, 'error');
  }
}
